import React, { useContext, useMemo, useState } from 'react';
import styles from './MfSourcePicker.module.css';
import useTheme from 'config/useTheme';
import GuiContext from 'config/context/guiContext';
import PluginsContext from 'config/context/pluginsContext';
import { useMfImport } from 'features/compositions/context/MfImportContext';
import { useImportCatalog } from 'features/compositions/services/useImportCatalog';
import MfConnectionForm, { missingRequired } from './MfConnectionForm';
import { useInspectSource } from 'features/compositions/services/useInspectSource';
import { useBrowseTargets } from 'features/compositions/services/useBrowseTargets';
import MfTargetPicker from './MfTargetPicker';

/**
 * Izbor izvora — prvi korak čarobnjaka.
 *
 * Lista se ne piše ovde nego stiže iz `GET /api/imports/`, pa je uvek u
 * saglasnosti sa `manifest.js` na backendu. Zatečeno stanje je bilo suprotno:
 * dozvoljene ekstenzije ukucane u komponenti, a MySQL i Mongo van čarobnjaka,
 * u zasebnim dijalozima do kojih se dolazilo drugim putem.
 *
 * GRUPISANJE PO PLUGIN-u (05.10.2026)
 * -----------------------------------
 * Jedan plugin na lancu ume da ima VIŠE izvora istih podataka: MySQL se čita i
 * sa živog servera i iz `mysqldump` fajla, a to je na lancu isti id 11.
 * Korisnik zato prvo bira plugin, pa način — inače bi mu u istom redu stajala
 * dva dugmeta koja znače jedno pravo, bez ikakve naznake da su u srodstvu.
 *
 * Pravilo je po ID-u, ne po imenu, jer je id ono što na lancu daje pravo.
 */

/**
 * Prva četiri bajta kažu da li je fajl ZIP arhiva.
 *
 * Čita se SADRŽAJ, ne ime: `accept` u dijalogu je samo filter, a fajl sme da
 * se zove kako god. Pregledač ume da pročita parče fajla bez učitavanja celog,
 * pa ovo ništa ne košta.
 *
 * Služi samo prikazu — da se polje za izbor unosa ne nudi nad fajlom u kome
 * nema šta da se bira. Odluku o tome ŠTA fajl jeste i dalje donosi backend,
 * nad bajtovima koje je sam pročitao.
 */
const prvihCetiri = (file) => {
    // `Blob.arrayBuffer` je noviji (Safari 14+), a `FileReader` ga ima svuda —
    // uključujući i jsdom, u kome testovi i rade. Zato rezerva, a ne samo jedno.
    if (typeof file.arrayBuffer === 'function') {
        return file.slice(0, 4).arrayBuffer().then((b) => new Uint8Array(b));
    }
    return new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(new Uint8Array(r.result));
        r.onerror = () => rej(r.error);
        r.readAsArrayBuffer(file.slice(0, 4));
    });
};

const jeArhiva = async (file) => {
    if (!file) return false;
    try {
        const g = await prvihCetiri(file);
        return g[0] === 0x50 && g[1] === 0x4b &&
            ((g[2] === 0x03 && g[3] === 0x04) || (g[2] === 0x05 && g[3] === 0x06));
    } catch {
        // Ako se ni prva četiri bajta ne mogu pročitati, ne pogađamo: polje za
        // unose se neće nuditi, a backend će nad pravim bajtovima reći šta je.
        return false;
    }
};

/** Polja koja imaju smisla samo nad arhivom — ona koja nabrajaju njene unose. */
const samoZaArhivu = (p) =>
    (p?.config || []).filter((f) => f.browse === 'entries').map((f) => f.name);

/** „Live server" ili „Dump file" — način, kad plugin ima više od jednog. */
const nacin = (p) => (p.source === 'connection' ? 'Live server' : 'Dump file');

/**
 * Da li izvoru posle izbora treba još nešto.
 *
 * NE gleda se tip izvora nego ŠTA TRAŽI. CSV, JSON i XML traže samo fajl, pa
 * ih izbor fajla i završava; `delimiter` i `root` imaju podrazumevane
 * vrednosti, pa ih niko ne mora dirati. Dump traži i tabele, konekcija traži
 * kredencijale — oba dobijaju formu.
 */
const trebaForma = (p) =>
    (p?.config || []).some((f) => f.type !== 'file' && (f.required || f.browse));

/** Zajednički početak imena u grupi — naslov kartice („MySQL Import"/„MySQL Dump" → „MySQL"). */
const naslovGrupe = (varijante) => {
    const reci = varijante.map((p) => String(p.name).split(' '));
    const zajednicke = [];
    for (let i = 0; i < reci[0].length; i++) {
        const r = reci[0][i];
        if (reci.every((x) => x[i] === r)) zajednicke.push(r); else break;
    }
    return zajednicke.join(' ') || varijante[0].name;
};

const MfSourcePicker = ({ onSubmit, loading }) => {
    const { dark } = useTheme();
    const { plugin, setPlugin, config, setConfig, file, setFile } = useMfImport();
    const { catalog, loading: catalogLoading, error: catalogError } = useImportCatalog();

    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = React.useRef(null);
    const izvorniFajlRef = React.useRef(null);
    // Koji izvor čeka da mu se izabere fajl. Dijalog se otvara pre nego što se
    // zna ima li fajla, pa se izbor mora zapamtiti do `onChange`.
    const [cekaFajl, setCekaFajl] = useState(null);
    // Da li je izabrani fajl arhiva — odlučuje samo o tome koja se polja nude.
    const [arhiva, setArhiva] = useState(false);

    // Pita server šta traži čim se unese adresa — pa se polja koja na tom
    // serveru ne postoje uklone pre nego što ih korisnik popuni.
    const { inspect, reset: resetInspect, result: inspected, checking, hide } = useInspectSource();

    // Nabrajanje onoga što u izvoru postoji. Podatke drži ovaj korak, a ne
    // forma — forma je generisana iz šeme i ne treba da zna ni za jednu rutu.
    const { browse, reset: resetTargets, targets, note, error: targetError, loading: browsing }
        = useBrowseTargets();
    const [browseField, setBrowseField] = useState(null);

    /**
     * Izvori poređani po plugin-u. Red je red iz kataloga, dakle iz manifesta.
     */
    const grupe = useMemo(() => {
        const po = new Map();
        for (const p of catalog) {
            if (!po.has(p.id)) po.set(p.id, []);
            po.get(p.id).push(p);
        }
        return [...po.values()];
    }, [catalog]);

    // Grupa u kojoj svi izvori traže samo fajl — to je „MultiFormat" (csv, json,
    // xml), i njih pokriva JEDNO dugme, jer se razlikuju samo po ekstenziji.
    const samoFajl = useMemo(
        () => grupe.find((g) => g.every((p) => p.source === 'file' && !trebaForma(p))) || [],
        [grupe]);

    const fileExtensions = samoFajl
        .map((p) => p.slug.replace(/-import$/, ''))
        .map((e) => `.${e}`);

    /**
     * Koji izvori su korisniku zaista na raspolaganju.
     *
     * Katalog stiže iz manifesta i nabraja SVE migrirane izvore, bez obzira na
     * to šta je korisnik instalirao. Do 28.09.2026. se ta razlika nigde nije
     * videla: ulaz u čarobnjak je zavisio samo od plugina 6, pa je onaj ko je
     * uključio MySQL (11) ostajao bez ulaza, a onaj ko je uključio 6 dobijao
     * sve izvore odjednom.
     *
     * Nedostupan izvor se NE sakriva. Sakriven izbor ne objašnjava zašto ga
     * nema; ovako klik kaže šta fali i gde se uključuje.
     *
     * Ovo je urednost prikaza, a NE kontrola prava: uvozne rute na backendu
     * pravo ne proveravaju (vidi `requirePlugin.js`), pa `curl` ovo zaobilazi.
     */
    const { userData: { guiname } } = useContext(GuiContext);
    const { getActiveByAccountName } = useContext(PluginsContext);
    const aktivni = getActiveByAccountName?.(guiname)?.active || [];
    const dostupan = (id) => aktivni.map(Number).includes(Number(id));

    const [nedostupanIzvor, setNedostupanIzvor] = useState(null);
    const javiDaNedostaje = (ime) =>
        setNedostupanIzvor(
            `${ime} is not installed and enabled for this account. ` +
            `Open Plugins, install it and press Enable, then come back here.`,
        );

    /** Zajedničko za svaki izbor izvora: čisti se ono što je ostalo od prethodnog. */
    const pocniSa = (p, noviFajl = null) => {
        setNedostupanIzvor(null);
        setArhiva(false);
        setPlugin(p);
        setFile(noviFajl);
        setConfig({});
        resetInspect();
        resetTargets();
        setBrowseField(null);
    };

    const izaberiIzvor = (p) => {
        if (!dostupan(p.id)) return javiDaNedostaje(p.name);
        // Izvor nad fajlom prvo traži fajl; forma dolazi posle, kad se zna šta
        // je u njemu. Obrnuto bi značilo da korisnik bira tabele iz fajla koji
        // još nije dao.
        if (p.source === 'file') { setCekaFajl(p); izvorniFajlRef.current?.click(); return; }
        pocniSa(p);
    };

    /**
     * Spisak onoga što u izvoru postoji — klik na „Browse", ili samo od sebe
     * posle izbora dump fajla.
     *
     * Šalju se sva popunjena polja OSIM onog koje se pretražuje — ono je ovde
     * ishod, a ne uslov. Plugin i fajl se primaju IZRIČITO jer se zove i
     * neposredno posle `pocniSa`, pre nego što se stanje osveži.
     */
    const otvoriSpisak = async (field, p = plugin, f = file) => {
        setBrowseField(field);
        const usable = (p?.config || [])
            .filter((f) => f.type !== 'file' && !hide.includes(f.name) && f.name !== field.name);
        const payload = usable.reduce((acc, f) => {
            const v = config[f.name] !== undefined && config[f.name] !== '' ? config[f.name] : f.default;
            if (v !== undefined && v !== '') acc[f.name] = v;
            return acc;
        }, {});

        // Kod izvora nad fajlom tabele se čitaju IZ fajla, pa mora i on; a
        // `field.browse` kaže KOJI nivo se traži — unosi arhive ili tabele.
        const found = await browse(p.slug, payload, f || undefined, field.browse);
        // Neuspeh zatvara popup, a greška se ispisuje uz formu — inače bi
        // korisnik gledao prazan spisak i ne bi znao zašto.
        if (found === null) setBrowseField(null);
    };

    /** Fajl izvor bez forme — izbor fajla je ceo unos, pa se odmah šalje. */
    const pickFile = (f) => {
        if (!f) return;
        const ext = String(f.name).split('.').pop().toLowerCase();
        const match = samoFajl.find((p) => p.slug === `${ext}-import`);
        if (!match) {
            alert(`Only ${fileExtensions.join(', ')} files are supported.`);
            return;
        }
        pocniSa(match, f);
        onSubmit({ slug: match.slug, file: f });
    };

    const posalji = () => {
        // Sakrivena polja se ne šalju — server ih ne razume, a korisnik ih ni
        // nije video da bi ih namerno postavio. Polje tipa `file` nikad ne ide
        // kroz `config`: ono putuje kao sam fajl.
        const usable = (plugin?.config || [])
            .filter((f) => f.type !== 'file' && !sakrij.includes(f.name));
        const missing = missingRequired(usable, config);
        if (missing.length) {
            // Polje koje se pretražuje ne treba kucati — treba ga izabrati. Bez
            // ove dopune poruka je tačna a slepa: kaže šta fali, ne i odakle se
            // uzima.
            const pretrazivo = usable.some((f) => f.browse && missing.includes(f.label || f.name));
            alert(`Missing: ${missing.join(', ')}`
                + (pretrazivo ? '\n\nPress Browse to list what the source contains.' : ''));
            return;
        }
        // Podrazumevane vrednosti iz šeme se šalju izričito — backend ih ima
        // svoje, ali korisnik treba da pošalje ono što na ekranu i vidi.
        const withDefaults = usable.reduce((acc, f) => {
            const v = config[f.name] !== undefined && config[f.name] !== '' ? config[f.name] : f.default;
            if (v !== undefined && v !== '') acc[f.name] = v;
            return acc;
        }, {});
        onSubmit({ slug: plugin.slug, config: withDefaults, file: file || undefined });
    };

    if (catalogLoading) return <div className={styles.hint}>Reading available import sources…</div>;
    if (catalogError) return <div className={styles.hint}>{catalogError}</div>;

    // Polje koje nabraja unose arhive nema smisla nad fajlom u kome nema šta da
    // se bira, pa se ne nudi. Isti spisak odlučuje i o slanju (`posalji`), da
    // se ne pošalje ono što korisnik nije ni video.
    const sakrij = [...hide, ...(arhiva ? [] : samoZaArhivu(plugin))];
    const poljaForme = (plugin?.config || [])
        .filter((f) => f.type !== 'file' && !sakrij.includes(f.name));
    const prihvata = (p) => (p?.config || []).find((f) => f.type === 'file')?.accept;

    return (
        <div className={`${styles.source_picker} ${dark ? styles.dark : ''}`}>
            <div className={styles.sources}>
                {grupe.map((g) => {
                    // Grupa „samo fajl" je jedno dugme: razlikuju se po ekstenziji.
                    if (g === samoFajl) {
                        return (
                            <button
                                key="file"
                                type="button"
                                aria-disabled={!dostupan(g[0]?.id)}
                                className={`${styles.source} ${plugin && !trebaForma(plugin) && plugin.source === 'file' ? styles.selected : ''} ${dostupan(g[0]?.id) ? '' : styles.unavailable}`}
                                onClick={() => {
                                    if (!dostupan(g[0]?.id)) return javiDaNedostaje('File import');
                                    setNedostupanIzvor(null);
                                    setPlugin(null); setConfig({}); fileInputRef.current?.click();
                                }}
                            >
                                File ({fileExtensions.join(' ')})
                            </button>
                        );
                    }

                    // Jedan izvor — jedno dugme, kao i do sada.
                    if (g.length === 1) {
                        const p = g[0];
                        return (
                            <button
                                key={p.slug}
                                type="button"
                                aria-disabled={!dostupan(p.id)}
                                className={`${styles.source} ${plugin?.slug === p.slug ? styles.selected : ''} ${dostupan(p.id) ? '' : styles.unavailable}`}
                                onClick={() => izaberiIzvor(p)}
                            >
                                {p.name}
                            </button>
                        );
                    }

                    // Više načina za isti plugin — kartica sa izborom unutra.
                    return (
                        <div key={`g${g[0].id}`} className={`${styles.card} ${dostupan(g[0].id) ? '' : styles.unavailable}`}>
                            <span className={styles.card_title}>{naslovGrupe(g)}</span>
                            <div className={styles.variants}>
                                {g.map((p) => (
                                    <button
                                        key={p.slug}
                                        type="button"
                                        aria-disabled={!dostupan(p.id)}
                                        className={`${styles.variant} ${plugin?.slug === p.slug ? styles.selected : ''}`}
                                        onClick={() => izaberiIzvor(p)}
                                    >
                                        {nacin(p)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {nedostupanIzvor && <p className={styles.warn}>{nedostupanIzvor}</p>}

            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept={fileExtensions.join(',')}
                onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = null; }}
            />
            {/* Zaseban birač za izvore nad fajlom koji traže još nešto: njihov
                `accept` dolazi iz šeme (`configSchema`), ne iz imena izvora. */}
            <input
                type="file"
                ref={izvorniFajlRef}
                style={{ display: 'none' }}
                accept={prihvata(cekaFajl) || undefined}
                onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = null;
                    const izvor = cekaFajl;
                    setCekaFajl(null);
                    if (!f || !izvor) return;
                    pocniSa(izvor, f);

                    // Arhiva ili ne — odlučuje samo koje se polje nudi i koji
                    // se spisak otvara prvi.
                    const uArhivi = await jeArhiva(f);
                    setArhiva(uArhivi);

                    // Spisak se otvara SAM. Kod konekcije korisnik mora prvo da
                    // unese kredencijale, pa je „Browse" na klik; kod dumpa je
                    // sve već u fajlu, pa bi traženje da sam pogodi imena bilo
                    // traženje da prepiše ono što mi već znamo. Pre ovoga je
                    // „Read structure" javljao samo „Missing: Tables", bez ijedne
                    // reči o tome gde se te tabele uzimaju (05.10.2026).
                    //
                    // KOJI spisak: kod arhive prvo FAJLOVI u njoj, pa tek posle
                    // tabele u izabranom. Kod običnog fajla nema prvog nivoa.
                    const polja = izvor.config || [];
                    const polje = uArhivi
                        ? polja.find((x) => x.browse === 'entries') || polja.find((x) => x.browse)
                        : polja.find((x) => x.browse && x.browse !== 'entries');
                    if (polje && izvor.targets) otvoriSpisak(polje, izvor, f);
                }}
            />

            {plugin && trebaForma(plugin) ? (
                <div className={styles.connection_area}>
                    {/* Fajl je već izabran; ime se pokazuje da se zna nad čim se
                        bira, i da promašen fajl ne ostane neprimećen. */}
                    {file && <p className={styles.info}>{file.name}</p>}
                    <MfConnectionForm
                        schema={poljaForme}
                        values={config}
                        hide={sakrij}
                        onBrowse={plugin.targets ? otvoriSpisak : undefined}
                        browsing={browsing ? browseField?.name : undefined}
                        onChange={(name, value) => {
                            const next = { ...config, [name]: value };
                            setConfig(next);
                            if (plugin.inspect && (name === 'host' || name === 'port')) {
                                inspect(plugin.slug, next);
                            }
                        }}
                    />
                    {checking && <p className={styles.hint}>Asking the server…</p>}
                    {targetError && <p className={styles.warn}>{targetError}</p>}
                    {inspected?.message && (
                        <p className={inspected.status === false ? styles.warn : styles.info}>
                            {inspected.message}
                        </p>
                    )}
                    <button
                        type="button"
                        className={styles.primary}
                        disabled={loading}
                        onClick={posalji}
                    >
                        {loading ? 'Reading…' : 'Read structure'}
                    </button>

                    {browseField && targets && (
                        <MfTargetPicker
                            targets={targets}
                            note={note}
                            initial={String(config[browseField.name] || '')
                                .split(',').map((s) => s.trim()).filter(Boolean)}
                            onCancel={() => { setBrowseField(null); resetTargets(); }}
                            onConfirm={(names) => {
                                // Isti oblik koji polje i prima kad se kuca:
                                // imena razdvojena zarezom.
                                setConfig({ ...config, [browseField.name]: names.join(', ') });
                                setBrowseField(null);
                                resetTargets();
                            }}
                        />
                    )}
                </div>
            ) : (
                <div
                    className={`${styles.dropzone} ${dragOver ? styles.active : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        pickFile(e.dataTransfer.files?.[0]);
                    }}
                >
                    Import or drag your file
                </div>
            )}
        </div>
    );
};

export default MfSourcePicker;
